import { defineComponent as _defineComponent } from 'vue'
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { useLanguageStore } from '@/helpers/composables/useStores';


export default /*#__PURE__*/_defineComponent({
  __name: 'FlatRentBuy',
  props: {
    title: null,
    offers: null,
    seasonNumber: null
  },
  setup(__props: any) {

const props = __props as {
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
};

const { language } = useLanguageStore();



const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Tällä hetkellä voit katsoa elokuvan',
					'suoratoistona palvelussa'
				),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Voit myös ostaa elokuvan', 'palvelussa'),
				getOfferTextBlock(MonetizationType.Rent, ' tai ladata tai vuokrata sen netissä palvelussa'),
				'. ',
			];
		case 'ru':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В настоящее время Вы можете посмотреть', 'на'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Также возможно купить и скачать', 'на'),
				getOfferTextBlock(MonetizationType.Rent, ' или взять напрокат на', 'онлайн'),
				'. ',
			];
		case 'it':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Adesso puoi guardare', 'in streaming su'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Puoi anche comprare', 'scaricandolo da'),
				getOfferTextBlock(MonetizationType.Rent, ' o noleggiarlo online su'),
				'. ',
			];
		case 'de':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Du kannst', 'bei', 'legal im Stream anschauen'),
				getOfferTextBlock(MonetizationType.Rent, ', bei', 'online leihen'),
				getOfferTextBlock(MonetizationType.Buy, ' oder auch bei', 'als Download kaufen'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Você pode assistir', 'no', 'legalmente online'),
				getOfferTextBlock(MonetizationType.Rent, ', no', 'alugar online'),
				getOfferTextBlock(MonetizationType.Buy, ' ou também no', 'para comprar o Download'),
				'. ',
			];
		case 'fr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'En ce moment, vous pouvez regarder', 'en streaming sur'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Il est également possible de louer', 'sur', 'en ligne'),
				getOfferTextBlock(MonetizationType.Buy, ' ou de le télécharger sur'),
				'. ',
			];
		case 'es':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Actualmente, usted es capaz de ver', 'streaming en'),
				'. ',
				getInfoTextBlock(
					MonetizationType.Rent,
					'Por lo tanto, es posible alquilar',
					'en',
					'como descarga online'
				),
				getOfferTextBlock(MonetizationType.Buy, ' y para comprarlo en'),
				'. ',
			];
		case 'ja':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					`現在の「${props.title}」の視聴情報：`,
					'は',
					'で配信中です。'
				),
				getOfferTextBlock(MonetizationType.Rent, '', 'で配信版のレンタルができます。'),
				getOfferTextBlock(MonetizationType.Buy, '配信版を購入してダウンロードする場合は', 'です'),
				'. ',
			];
		case 'ko':
			return [
				getOfferTextBlock(
					MonetizationType.Flatrate,
					'현재 ',
					`에서 "${props.title}" 스트리밍 서비스 중입니다. `
				),
				getOfferTextBlock(MonetizationType.Rent, '또한 ', '에서 대여가 가능하며 '),
				getOfferTextBlock(MonetizationType.Buy, '중이며 ', '에서 유료 다운로드하실 수 있습니다.'),
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'În prezent, puteți viziona', 'streaming pe'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Puteți, de asemenea, să cumpărați', 'pe', 'prin descărcare'),
				getOfferTextBlock(MonetizationType.Rent, ' sau să-l închiriați pe', 'online'),
				'. ',
			];
		case 'hu':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Jelenleg a(z)', 'online megtekinthető itt:'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Továbbá a(z)', 'megvásárolható a(z)', 'szolgáltatónál'),
				getOfferTextBlock(MonetizationType.Rent, ' vagy online kibérelhető itt:'),
				'. ',
			];
		case 'cs':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktuálně máte možnost sledovat', 'streaming u'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, ' Si můžete koupit', 'u', 'ke stažení'),
				getOfferTextBlock(MonetizationType.Rent, ' nebo si jej půjčit u', 'online'),
				'. ',
			];
		case 'bg':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В момента можете да гледате стрийминг на', 'в', 'онлайн'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Възможно е също да купите', 'в', 'като изтеглена версия'),
				getOfferTextBlock(MonetizationType.Rent, ' или да го наемете', 'онлайн'),
				'. ',
			];
		case 'ar':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'يمكنكم حالياً مشاهدة', 'على'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'بإمكانكم أيضاً شراء', 'على', 'وتنزيله'),
				getOfferTextBlock(MonetizationType.Rent, ' أو تأجيره على', 'أونلاين'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Atualmente você pode assistir', 'em streaming via'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Também é possível comprar', 'via', 'como download'),
				getOfferTextBlock(MonetizationType.Rent, ' ou alugar online via'),
				'. ',
			];
		case 'tr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Şu an', 'adlı yapımı', 'üzerinden izleyebilirsiniz'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Ayrıca', 'içeriğini', 'üzerinden satın alıp indirebilir'),
				getOfferTextBlock(
					MonetizationType.Rent,
					' veya',
					'üzerinden kiralayıp çevrimiçi olarak seyredebilirsiniz'
				),
				'. ',
			];
		case 'sq':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktualisht, ju mund të shikoni', 'në'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Gjithashtu mund ta blini', 'si shkarkim në'),
				getOfferTextBlock(MonetizationType.Rent, ' ose ta merrni me qira në', 'online'),
				'. ',
			];
		case 'pl':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Obecnie możesz oglądać', 'w streamingu w serwisie'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Możesz również kupić', 'jako plik do pobrania w serwisie'),
				getOfferTextBlock(MonetizationType.Rent, ' lub wypożyczyć na', 'online'),
				'. ',
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Acum poți să vezi', 'pe'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Poți de asemenea să cumperi și să descarci', 'pe'),
				getOfferTextBlock(MonetizationType.Rent, ' sau să-l închiriezi online pe'),
				'. ',
			];
		case 'az':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Hazırda siz', 'üzərindən', 'yayımını izləyə bilərsiniz'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Həmçinin', 'filmini', 'vasitəsilə alaraq endirə bilərsiniz'),
				getOfferTextBlock(MonetizationType.Rent, ' və ya', 'saytında onlayn icarəyə götürə bilərsiniz'),
				'. ',
			];
		case 'el':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
					'με streaming στο'
				),
				'. ',
				getInfoTextBlock(
					MonetizationType.Buy,
					'Υπάρχει επίσης η δυνατότητα να αγοράσετε τον τίτλο',
					'στο',
					'ως αρχείο λήψης'
				),
				getOfferTextBlock(MonetizationType.Rent, ' ή να τον ενοικιάσετε στο', 'online'),
				'. ',
			];
		case 'sr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Trenutno možete da gledate', 'strim na'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Takođe je moguće kupiti', 'na', 'kao preuzimanje'),
				getOfferTextBlock(MonetizationType.Rent, ' ili iznajmite na', 'onlajn'),
				'. ',
			];
		case 'sv':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'För närvarande kan du se', 'streama på'),
				getInfoTextBlock(MonetizationType.Buy, ' eller köpa', 'på', 'som nedladdning'),
				getOfferTextBlock(MonetizationType.Rent, ' eller hyra den på', 'online'),
				'. ',
			];
		case 'uk':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Наразі ви можете дивитися трансляцію', 'на'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'Ви також можете придбати і завантажити', 'на'),
				getOfferTextBlock(MonetizationType.Rent, ' або взяти її напрокат онлайн на'),
				'. ',
			];
		case 'ur':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'فی الحال آپ', 'پر'),
				getRTLInfoTextBlock(
					MonetizationType.Buy,
					'اسٹریمنگ دیکھنے کے قابل ہیں۔ ڈاؤن لوڈ کے طور پر ',
					'پر',
					'خریدنا بھی ممکن ہے۔'
				),
				getOfferTextBlock(MonetizationType.Rent, ' یا اسے آن لائن', 'پر کرائے پر لیں۔'),
			];
		case 'en':
		default:
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Currently you are able to watch', 'streaming on'),
				'. ',
				getInfoTextBlock(MonetizationType.Buy, 'It is also possible to buy', 'on', 'as download'),
				getOfferTextBlock(MonetizationType.Rent, ' or rent it on', 'online'),
				'. ',
			];
	}
});

return { __sfc: true,language, props, getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock, text }
}

})